import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { forgotPasswordApi, toAuthResult } from "@/portals/patient/api/patientAuthApi";
import { saveAuthFlowState } from "./authFlowState";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { AuthTextField } from "@/shared/components/AuthField";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { validateFields } from "@/shared/lib/validators";

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validateFields(
      { email },
      {
        email: [
          { required: true, message: "Email is required" },
          { type: "email", message: "Enter a valid email address" },
        ],
      },
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setLoading(true);
    const result = toAuthResult(await forgotPasswordApi(email));
    setLoading(false);

    if (result.success) {
      saveAuthFlowState({ email });
      toastSuccess("OTP has been sent successfully. Please check your email.");
      navigate("/patient/auth/verify-reset-otp", { state: { email } });
    } else {
      toastError(result.error ?? "Unable to send code.");
    }
  };

  return (
    <div className="w-full max-w-sm">
      <h2 className="text-[28px] font-normal leading-tight text-[#0A2947]">
        Forgot your <span className="font-extrabold tracking-tight">password?</span>
      </h2>
      <p className="!mt-2 !mb-8 text-sm text-[#9CA3AF]">
        Enter your email and we'll send you a code to reset it.
      </p>

      <form onSubmit={handleSubmit}>
        <AuthTextField
          label="Email"
          placeholder="Enter your email"
          value={email}
          onChange={(v) => { setEmail(v); clearError("email"); }}
          error={errors.email}
          className="mb-6"
        />

        <AuthSubmitButton loading={loading}>Send OTP</AuthSubmitButton>
      </form>

      <p className="mt-10 text-center text-sm text-[#9CA3AF]">
        Remembered your password?{" "}
        <Link to="/patient/auth/signin" className="font-semibold text-[#E4002B] hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}
